import React, { useState, useEffect } from "react";
import { useAuth, api } from "../context/AuthContext";
import SuccessModal from "../components/SuccessModal";
import {
  Container,
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import {
  Phone,
  Email,
  LocationOn,
  Send,
  Security,
  FlashOn,
  HeadsetMic,
  Person,
  Edit,
  Info,
  AccessTime,
} from "@mui/icons-material";
import contact from "../Assests/Images/futuristic-contact.png";
import axios from "axios";

function Contact() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
    phoneNumber: user?.phone || '',
    subject: 'Contact Us Inquiry',
    message: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        firstName: prev.firstName || user.first_name || '',
        lastName: prev.lastName || user.last_name || '',
        email: prev.email || user.email || '',
        phoneNumber: prev.phoneNumber || user.phone || ''
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phoneNumber') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        setFormData((prev) => ({ ...prev, [name]: numericValue }));
      }
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateEmail = (email) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.firstName || !formData.email || !formData.message) {
      setError('Please fill in all required fields.');
      return;
    }

    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (formData.phoneNumber && formData.phoneNumber.length !== 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }

    setLoading(true);

    try {
      await api.post(
        '/support-queries',
        {
          subject: formData.subject,
          message: formData.message,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          userId: user?.id,
        }
      );

      setSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        subject: 'Contact Us Inquiry',
        message: '',
      });
    } catch (err) {
      console.error('Failed to send message:', err);
      const backendMessage = err.response?.data?.message;
      setError(backendMessage || 'Failed to send message. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
  };

  const contactInfo = [
    {
      icon: <Phone sx={{ fontSize: 20 }} />,
      title: "Phone",
      content: "+91 62815 97586",
    },
    {
      icon: <Email sx={{ fontSize: 20 }} />,
      title: "Email",
      content: "connect@exihire.com",
    },
    {
      icon: <LocationOn sx={{ fontSize: 20 }} />,
      title: "Address",
      content: "Bhoomatha Thalluri Heights, Kankipadu, Vijayawada-521151",
    },
    {
      icon: <AccessTime sx={{ fontSize: 20 }} />,
      title: "Support",
      content: "24/7 Support Available",
    },
  ];

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      bgcolor: 'white',
      borderRadius: '12px',
      height: '50px',
      fontSize: '0.95rem',
      '& fieldset': {
        borderColor: '#E5E7EB',
        transition: 'all 0.2s ease-in-out',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: '#1E40AF',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1E40AF',
        borderWidth: '1px',
        boxShadow: '0 0 0 4px rgba(67, 56, 202, 0.1)',
      },
      '& .MuiInputAdornment-root .MuiSvgIcon-root': {
        color: '#1E3A8A',
        fontSize: '1.2rem',
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '12px 14px 12px 8px', // Added some padding left to separate from icon if needed
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.95rem',
      transform: 'translate(40px, 14px) scale(1)',
      '&.Mui-focused, &.MuiFormLabel-filled': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#1E40AF',
    },
    margin: '4px 0',
  };

  const textareaStyles = {
    ...inputStyles,
    '& .MuiOutlinedInput-root': {
      ...inputStyles['& .MuiOutlinedInput-root'],
      height: 'auto',
      alignItems: 'flex-start',
      pt: 1.5,
    },
    '& .MuiInputLabel-root': {
      ...inputStyles['& .MuiInputLabel-root'],
      transform: 'translate(40px, 16px) scale(1)',
      '&.Mui-focused, &.MuiFormLabel-filled': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
    },
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: '#0A0F2C',
          color: 'white',
          pt: { xs: 3, md: 4 },
          pb: { xs: 4, md: 5 },
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: { xs: '100%', lg: '1200px', xxl: '1440px', xxxl: '1800px' } }}>
          <Grid container spacing={4} alignItems="center">
            {/* Left side content */}
            <Grid item xs={12} md={7}>
              <Box>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', sm: '3.5rem', md: '5rem' },
                    fontWeight: 900,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.1,
                    mb: 2,
                    textTransform: 'uppercase',
                    background: 'linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0.7) 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  CONTACT US
                </Typography>
                <Box
                  sx={{
                    width: 80,
                    height: 4,
                    background: 'linear-gradient(90deg, #1E40AF 0%, #3B82F6 100%)',
                    borderRadius: '2px',
                    mb: 4
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    color: 'rgba(255, 255, 255, 0.8)',
                    mb: 1,
                    fontWeight: 400
                  }}
                >
                  Reach out to ExiHire for support or inquiries.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    color: 'rgba(255, 255, 255, 0.8)',
                    mb: 6,
                    fontWeight: 400
                  }}
                >
                  Our team is here to assist you.
                </Typography>

                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {[
                    { icon: <HeadsetMic />, text: "Dedicated Support" },
                    { icon: <Security />, text: "Secure & Reliable" },
                    { icon: <FlashOn />, text: "Quick Response" }
                  ].map((item, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          bgcolor: 'rgba(67, 56, 202, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#1E3A8A',
                          border: '1px solid rgba(67, 56, 202, 0.3)'
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '1.1rem', color: 'white' }}>
                        {item.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

              <Grid
                  item
                  xs={12}
                  md={5}
                  sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                  }}
              >
                  <Box
                      component="img"
                      src={contact}
                      alt="Contact Hero"
                      sx={{
                          width: { xs: '100%', md: '90%' },
                          maxWidth: { xs: '100%', md: '650px' },
                          height: 'auto',
                          objectFit: 'contain',
                          borderRadius: '16px',
                          display: 'block',
                          ml: { md: 4 }, // move image slightly right
                      }}
                  />
              </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth={false} sx={{ maxWidth: { xs: '100%', lg: '1200px', xxl: '1440px', xxxl: '1800px' }, py: 10 }}>
        <Grid container spacing={4} alignItems="stretch">
          {/* Contact Form */}
          <Grid item xs={12} md={7}>
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(67, 56, 202, 0.3)',
                  }}
                >
                  <Send sx={{ fontSize: 20, transform: 'rotate(-45deg)', mt: -0.5, ml: 0.5 }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#111827",
                    fontWeight: 800,
                    fontSize: "1.35rem",
                    letterSpacing: "-0.01em",
                    textTransform: 'uppercase',
                  }}
                >
                  Send us <Box component="span" sx={{ color: "#1E40AF" }}>A Message</Box>
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 60,
                  height: 2,
                  background: "linear-gradient(90deg, #1E40AF 0%, #3B82F6 100%, transparent 100%)",
                  borderRadius: "2px",
                  ml: 7.2
                }}
              />
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box
              sx={{
                p: 4,
                borderRadius: "16px",
                border: "1px solid #F3F4F6",
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05)",
                bgcolor: "white",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <form onSubmit={handleSubmit} style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      sx={inputStyles}
                      InputProps={{
                        startAdornment: (
                          <Person sx={{ mr: 1, color: '#1E3A8A' }} />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      sx={inputStyles}
                      InputProps={{
                        startAdornment: (
                          <Person sx={{ mr: 1, color: '#1E3A8A' }} />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      sx={inputStyles}
                      InputProps={{
                        startAdornment: (
                          <Email sx={{ mr: 1, color: '#1E3A8A' }} />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      sx={inputStyles}
                      InputProps={{
                        startAdornment: (
                          <Phone sx={{ mr: 1, color: '#1E3A8A' }} />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      sx={inputStyles}
                      InputProps={{
                        startAdornment: (
                          <Info sx={{ mr: 1, color: '#1E3A8A' }} />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Your Message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      multiline
                      rows={4}
                      required
                      sx={textareaStyles}
                      InputProps={{
                        startAdornment: (
                          <Edit sx={{ mr: 1, color: '#1E3A8A', mt: 0.5 }} />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ mt: 'auto' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={loading}
                      sx={{
                        mt: 2,
                        py: 1.5,
                        borderRadius: "12px",
                        background: "linear-gradient(90deg, #1E40AF 0%, #3B82F6 100%)",
                        boxShadow: "0 4px 15px rgba(67, 56, 202, 0.4)",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "1rem",
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background: "linear-gradient(90deg, #1D4ED8 0%, #2563EB 100%)",
                          boxShadow: "0 8px 25px rgba(67, 56, 202, 0.5)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : (
                        <>
                          <Send sx={{ fontSize: 20, transform: 'rotate(-45deg)', mt: -0.5 }} />
                          <span>Send Message</span>
                        </>
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={5}>
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(67, 56, 202, 0.3)',
                  }}
                >
                  <Info sx={{ fontSize: 20 }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#111827",
                    fontWeight: 800,
                    fontSize: "1.35rem",
                    letterSpacing: "-0.01em",
                    textTransform: 'uppercase',
                  }}
                >
                  Contact <Box component="span" sx={{ color: "#1E40AF" }}>Information</Box>
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 60,
                  height: 2,
                  background: "linear-gradient(90deg, #1E40AF 0%, #3B82F6 100%, transparent 100%)",
                  borderRadius: "2px",
                  ml: 7.2
                }}
              />
            </Box>
            <Box
              sx={{
                p: 3,
                borderRadius: "16px",
                border: "1px solid #F3F4F6",
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05)",
                bgcolor: "white",
                height: "auto",
              }}
            >
              {contactInfo.map((info, index) => (
                <React.Fragment key={index}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      py: 1.8,
                    }}
                  >
                    <Box
                      sx={{
                        background: "linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)",
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        boxShadow: "0 4px 10px rgba(67, 56, 202, 0.2)",
                        flexShrink: 0,
                      }}
                    >
                      {info.icon}
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#111827",
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          display: "block",
                          mb: 0.2,
                        }}
                      >
                        {info.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#6B7280",
                          fontWeight: 500,
                          fontSize: "0.85rem",
                          lineHeight: 1.5,
                          whiteSpace: 'pre-line'
                        }}
                      >
                        {info.content}
                      </Typography>
                    </Box>
                  </Box>
                  {index < contactInfo.length - 1 && (
                    <Box
                      sx={{
                        height: "1px",
                        background: "linear-gradient(90deg, #F3F4F6 0%, transparent 100%)",
                        width: "100%",
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Success Modal */}
      <SuccessModal
        open={submitted}
        handleClose={handleClose}
        title="Thank you for your message!"
        message="Your inquiry has been successfully submitted. Our team will review it and respond to you as soon as possible."
      />
    </Box>
  );
}

export default Contact;